import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  Inbox,
  Send,
  FileText,
  Settings,
  Edit3,
  Folder,
  Plus,
  X,
  Calendar,
  Trash2,
  AlertTriangle,
  PanelLeftClose,
  PanelLeftOpen,
} from "lucide-react";
import cloudflareApi from "../lib/cloudflare-api";
import { getCloudflareConfig } from "../lib/storage";

interface SidebarProps {
  selectedFolder: string;
  onSelectFolder: (folder: string) => void;
  onCompose: () => void;
  onSettings: () => void;
  onCalendar: () => void;
  isOnline: boolean;
}

interface FolderCounts {
  inbox: number;
  inbox_unread: number;
  sent: number;
  drafts: number;
  spam?: number;
  trash?: number;
}

interface StorageInfo {
  used: number;
  total: number;
  percentage: number;
}

const sidebarSpring = { type: "spring" as const, stiffness: 500, damping: 30 };

export default function Sidebar({ selectedFolder, onSelectFolder, onCompose, onSettings, onCalendar, isOnline }: SidebarProps) {
  const [counts, setCounts] = useState<FolderCounts>({
    inbox: 0,
    inbox_unread: 0,
    sent: 0,
    drafts: 0,
    spam: 0,
    trash: 0,
  });
  const [customFolders, setCustomFolders] = useState<string[]>([]);
  const [userEmail, setUserEmail] = useState<string>("");
  const [isAddingFolder, setIsAddingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [storage, setStorage] = useState<StorageInfo>({ used: 0, total: 1000000000, percentage: 0 });
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    loadCounts();
    loadCustomFolders();
    loadUserEmail();
    loadStorageInfo();
    const interval = setInterval(() => { loadCounts(); }, 5000);
    return () => clearInterval(interval);
  }, []);

  const loadCounts = async () => {
    try {
      const result = await cloudflareApi.getEmailCounts();
      setCounts({
        inbox: result.counts.inbox?.total || 0,
        inbox_unread: result.counts.inbox?.unread || 0,
        sent: result.counts.sent?.total || 0,
        drafts: result.counts.drafts?.total || 0,
        spam: result.counts.spam?.total || 0,
        trash: result.counts.trash?.total || 0,
      });
    } catch (error) {
      console.error("Error loading counts:", error);
    }
  };

  const loadCustomFolders = async () => {
    try {
      const result = await cloudflareApi.getFolders();
      setCustomFolders(result.folders);
    } catch (error) {
      console.error("Error loading custom folders:", error);
    }
  };

  const loadUserEmail = () => {
    try {
      const config = getCloudflareConfig();
      if (config.user_email) setUserEmail(config.user_email);
    } catch (error) {
      console.error("Error loading user email:", error);
    }
  };

  const loadStorageInfo = async () => {
    try {
      const result = await cloudflareApi.getStorageInfo();
      const used = result.used_bytes || 0;
      const total = result.limit_bytes || 1000000000;
      const percentage = result.percent_used || 0;
      setStorage({ used, total, percentage });
    } catch (error) {
      console.error("Error loading storage info:", error);
    }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      await cloudflareApi.createFolder(newFolderName);
      await loadCustomFolders();
      setNewFolderName("");
      setIsAddingFolder(false);
    } catch (error) {
      alert(`Error: ${error}`);
    }
  };

  const handleDeleteFolder = async (folderName: string) => {
    if (!confirm(`¿Eliminar la carpeta "${folderName}"?`)) return;
    try {
      await cloudflareApi.deleteFolder(folderName);
      await loadCustomFolders();
    } catch (error) {
      alert(`Error: ${error}`);
    }
  };

  const getInitials = (email: string) => {
    if (!email) return "U";
    const parts = email.split("@");
    return parts[0]?.substring(0, 2).toUpperCase() || "U";
  };

  const toggleSidebar = () => setIsOpen(!isOpen);

  const generalFolders = [
    { id: "inbox", label: "Recibidos", icon: Inbox, count: counts.inbox, unread: counts.inbox_unread },
    { id: "sent", label: "Enviados", icon: Send, count: counts.sent },
    { id: "drafts", label: "Borradores", icon: FileText, count: counts.drafts },
    { id: "spam", label: "Spam", icon: AlertTriangle, count: counts.spam || 0 },
    { id: "trash", label: "Papelera", icon: Trash2, count: counts.trash || 0 },
  ];

  return (
    <motion.div
      className="sidebar-rail"
      animate={{ width: isOpen ? 240 : 56 }}
      transition={sidebarSpring}
      style={{
        height: "100vh",
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        background: "var(--bg-sidebar)",
        borderRight: "1px solid var(--border)",
        overflow: "hidden",
        position: "relative",
        zIndex: 20,
      }}
    >
      {/* Compose button at top */}
      <div style={{ padding: isOpen ? "12px 12px 8px" : "12px 8px 8px", flexShrink: 0, display: "flex", justifyContent: "center" }}>
        <button
          onClick={onCompose}
          className="compose-btn"
          title="Redactar"
          style={{
            width: "100%",
            height: isOpen ? 40 : "auto",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            padding: isOpen ? "0 12px" : "7px 0",
            borderRadius: isOpen ? 12 : 8,
            border: "none",
            cursor: "pointer",
            background: "var(--primary)",
            color: "white",
            fontSize: 13,
            fontWeight: 600,
            transition: "all 0.2s ease",
            overflow: "hidden",
            whiteSpace: "nowrap",
          }}
        >
          <Edit3 size={17} style={{ flexShrink: 0 }} />
          {isOpen && <span>Redactar</span>}
        </button>
      </div>

      {/* Folder navigation */}
      <nav style={{ flex: 1, overflowY: "auto", overflowX: "hidden", padding: isOpen ? "4px 8px" : "4px 8px" }}>
        {/* General folders */}
        <div style={{ marginBottom: 4 }}>
          {isOpen && (
            <div style={{
              fontSize: 10,
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              color: "var(--text-tertiary)",
              padding: "8px 8px 4px",
            }}>
              General
            </div>
          )}
          {generalFolders.map((folder) => {
            const Icon = folder.icon;
            const isActive = selectedFolder === folder.id;
            return (
              <button
                key={folder.id}
                onClick={() => onSelectFolder(folder.id)}
                title={!isOpen ? folder.label : undefined}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: isOpen ? "7px 10px" : "7px 0px",
                  justifyContent: isOpen ? "flex-start" : "center",
                  background: isActive ? "var(--bg-selected)" : "transparent",
                  border: "none",
                  borderLeft: isActive ? "3px solid var(--primary)" : "3px solid transparent",
                  borderRadius: isOpen ? "8px" : "8px",
                  cursor: "pointer",
                  color: isActive ? "var(--primary)" : "var(--text-secondary)",
                  fontSize: 13,
                  fontWeight: isActive ? 500 : 400,
                  transition: "background 0.12s ease, color 0.12s ease",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  position: "relative",
                }}
              >
                <Icon size={18} style={{ flexShrink: 0, color: isActive ? "var(--primary)" : "var(--text-tertiary)" }} />
                {isOpen && (
                  <>
                    <span style={{ flex: 1, textAlign: "left" }}>{folder.label}</span>
                    {(folder.unread || 0) > 0 && (
                      <span style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: "var(--primary)",
                        minWidth: 18,
                        textAlign: "right",
                      }}>
                        {folder.unread}
                      </span>
                    )}
                    {!(folder.unread && folder.unread > 0) && folder.count > 0 && (
                      <span style={{
                        fontSize: 11,
                        color: "var(--text-muted)",
                        minWidth: 18,
                        textAlign: "right",
                      }}>
                        {folder.count}
                      </span>
                    )}
                  </>
                )}
                {!isOpen && (folder.unread || 0) > 0 && (
                  <span style={{
                    position: "absolute",
                    top: 4,
                    right: 6,
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    background: "var(--primary)",
                  }} />
                )}
              </button>
            );
          })}
        </div>

        {/* Custom folders */}
        <div>
          {isOpen && (
            <div style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "12px 8px 4px",
            }}>
              <span style={{
                fontSize: 10,
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                color: "var(--text-tertiary)",
              }}>
                Carpetas
              </span>
              <button
                onClick={() => setIsAddingFolder(!isAddingFolder)}
                title="Crear carpeta"
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: "var(--text-tertiary)",
                  display: "flex",
                  padding: 2,
                  borderRadius: 4,
                }}
              >
                <Plus size={14} />
              </button>
            </div>
          )}

          {/* New folder input */}
          <AnimatePresence>
            {isAddingFolder && isOpen && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.15 }}
                style={{ overflow: "hidden", padding: "0 4px" }}
              >
                <div style={{
                  display: "flex",
                  gap: 4,
                  padding: "4px 0",
                }}>
                  <input
                    type="text"
                    placeholder="Nombre"
                    value={newFolderName}
                    onChange={(e) => setNewFolderName(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && handleCreateFolder()}
                    autoFocus
                    style={{
                      flex: 1,
                      padding: "5px 8px",
                      fontSize: 12,
                      border: "1px solid var(--border)",
                      borderRadius: 6,
                      background: "var(--bg-content)",
                      color: "var(--text-primary)",
                      outline: "none",
                    }}
                  />
                  <button onClick={handleCreateFolder} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--primary)", display: "flex", padding: 4 }}>
                    <Plus size={14} />
                  </button>
                  <button onClick={() => setIsAddingFolder(false)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-tertiary)", display: "flex", padding: 4 }}>
                    <X size={14} />
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {customFolders.map((folder) => {
            const isActive = selectedFolder === folder;
            return (
              <div
                key={folder}
                style={{ display: "flex", alignItems: "center", position: "relative" }}
                className="folder-item-wrapper"
              >
                <button
                  onClick={() => onSelectFolder(folder)}
                  title={!isOpen ? folder : undefined}
                  style={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: isOpen ? "7px 10px" : "7px 0px",
                    justifyContent: isOpen ? "flex-start" : "center",
                    background: isActive ? "var(--bg-selected)" : "transparent",
                    border: "none",
                    borderLeft: isActive ? "3px solid var(--primary)" : "3px solid transparent",
                    borderRadius: 8,
                    cursor: "pointer",
                    color: isActive ? "var(--primary)" : "var(--text-secondary)",
                    fontSize: 13,
                    fontWeight: isActive ? 500 : 400,
                    transition: "background 0.12s ease, color 0.12s ease",
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                  }}
                >
                  <Folder size={18} style={{ flexShrink: 0, color: isActive ? "var(--primary)" : "var(--text-tertiary)" }} />
                  {isOpen && <span style={{ flex: 1, textAlign: "left" }}>{folder}</span>}
                </button>
                {isOpen && (
                  <button
                    onClick={() => handleDeleteFolder(folder)}
                    title="Eliminar"
                    className="folder-delete-btn"
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      color: "var(--text-tertiary)",
                      display: "flex",
                      padding: 4,
                      borderRadius: 4,
                      position: "absolute",
                      right: 4,
                      opacity: 0,
                      transition: "opacity 0.12s ease",
                    }}
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
            );
          })}

          {!isOpen && customFolders.length === 0 && (
            <button
              onClick={() => { setIsOpen(true); setIsAddingFolder(true); }}
              title="Crear carpeta"
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
                padding: "7px 0",
                background: "transparent",
                border: "none",
                borderRadius: 8,
                cursor: "pointer",
                color: "var(--text-tertiary)",
                transition: "color 0.12s ease",
              }}
            >
              <Plus size={18} />
            </button>
          )}
        </div>
      </nav>

      {/* Bottom section: storage bar + avatar + action icons */}
      <div style={{ flexShrink: 0, borderTop: "1px solid var(--border)" }}>
        {/* Storage: thin colored bar */}
        <div style={{ padding: isOpen ? "8px 14px 4px" : "8px 10px 4px" }}>
          <div style={{
            height: 3,
            background: "var(--bg-active)",
            borderRadius: 2,
            overflow: "hidden",
          }}>
            <div style={{
              height: "100%",
              width: `${storage.percentage}%`,
              background: storage.percentage >= 90 ? "#f43f5e" : storage.percentage >= 70 ? "#f59e0b" : "var(--primary)",
              borderRadius: 2,
              transition: "width 0.3s ease",
            }} />
          </div>
        </div>

        {/* Footer row: avatar, actions, sidebar toggle */}
        <div style={{
          display: "flex",
          flexDirection: "column",
          gap: 4,
          padding: isOpen ? "6px 10px 10px" : "6px 8px 10px",
          alignItems: "center",
        }}>
          {/* Row: avatar + action icons */}
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: 4,
            width: "100%",
            justifyContent: isOpen ? "flex-start" : "center",
            flexWrap: isOpen ? "nowrap" : "wrap",
          }}>
            {/* User avatar */}
            <div
              title={userEmail || "Usuario"}
              style={{
                width: 28,
                height: 28,
                borderRadius: "50%",
                background: "var(--primary)",
                color: "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 10,
                fontWeight: 600,
                flexShrink: 0,
                cursor: "default",
              }}
            >
              {getInitials(userEmail)}
            </div>

            {isOpen && (
              <>
                <span style={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  background: isOnline ? "var(--primary)" : "var(--text-muted)",
                  flexShrink: 0,
                  marginLeft: 2,
                }} />
                <span style={{ flex: 1 }} />
              </>
            )}

            <button
              onClick={onCalendar}
              title="Calendario"
              style={{
                width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center",
                background: "transparent", border: "none", borderRadius: 6, cursor: "pointer",
                color: "var(--text-muted)", transition: "color 0.12s ease, background 0.12s ease",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "var(--bg-hover)"; e.currentTarget.style.color = "var(--text-secondary)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "var(--text-muted)"; }}
            >
              <Calendar size={15} />
            </button>
            <button
              onClick={onSettings}
              title="Configuración"
              style={{
                width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center",
                background: "transparent", border: "none", borderRadius: 6, cursor: "pointer",
                color: "var(--text-muted)", transition: "color 0.12s ease, background 0.12s ease",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "var(--bg-hover)"; e.currentTarget.style.color = "var(--text-secondary)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "var(--text-muted)"; }}
            >
              <Settings size={15} />
            </button>
          </div>

          {/* Sidebar toggle button - always visible */}
          <button
            onClick={toggleSidebar}
            title={isOpen ? "Colapsar sidebar" : "Expandir sidebar"}
            style={{
              width: isOpen ? "100%" : 40,
              height: 28,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
              background: "var(--bg-hover)",
              border: "none",
              borderRadius: 6,
              cursor: "pointer",
              color: "var(--text-muted)",
              fontSize: 11,
              transition: "color 0.12s ease, background 0.12s ease, width 0.2s ease",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "var(--bg-active)"; e.currentTarget.style.color = "var(--text-secondary)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "var(--bg-hover)"; e.currentTarget.style.color = "var(--text-muted)"; }}
          >
            {isOpen ? <PanelLeftClose size={14} /> : <PanelLeftOpen size={14} />}
            {isOpen && <span>Colapsar</span>}
          </button>
        </div>
      </div>
    </motion.div>
  );
}
